import styled, { keyframes } from "styled-components";

// Animations
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideInFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;
const slideInFromRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-5px);
  }
`;

// Base Components
export const Container = styled.div`
  min-height: 100vh;
  background: var(--background);
  animation: ${fadeIn} 0.5s ease-out;
`;

export const Header = styled.header`
  background: var(--surface);
  border-bottom: 2px solid var(--accent-light);
  padding: 15px 0;
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: var(--shadow-soft);
  animation: ${slideIn} 0.6s ease-out;
  @media (max-width: 550px) {
    padding: 10px 0;
  }
`;

export const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 auto;
  max-width: 1440px;
  padding: 0 20px;
`;

export const Logo = styled.a`
  font-family: "Source Code Pro", monospace;
  font-size: 24px;
  font-weight: 700;
  color: var(--accent);
  text-decoration: none;
  letter-spacing: 2px;
  text-transform: uppercase;
  animation: ${float} 3s ease-in-out infinite;

  &::before {
    content: "♡ ";
    color: var(--accent-dark);
    font-size: 20px;
  }

  &::after {
    content: " ♡";
    color: var(--accent-dark);
    font-size: 20px;
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: 550px) {
    display: none;
  }
`;
export const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--gradient-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: var(--surface);
  font-weight: 700;
  border: 2px solid var(--accent-light);
`;

export const UserDetails = styled.div`
  h4 {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
  }

  p {
    font-size: 11px;
    color: var(--text-muted);
    text-transform: uppercase;
  }
`;

export const LogoutBtn = styled.button`
  background: transparent;
  border: 2px solid var(--accent-light);
  color: var(--text-secondary);
  padding: 10px 12px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
    transform: translateY(-2px);
  }
`;

export const Tooltip = styled.div`
  position: absolute;
  bottom: -35px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--text-primary);
  color: var(--surface);
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 11px;
  white-space: nowrap;
  opacity: ${(props) => (props.$show ? 1 : 0)};
  visibility: ${(props) => (props.$show ? "visible" : "hidden")};
  transition: all 0.3s ease;
  z-index: 1000;

  &::before {
    content: "";
    position: absolute;
    top: -4px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-bottom: 4px solid var(--text-primary);
  }
`;

export const MainLayout = styled.div`
  display: flex;
  flex-direction: row;
  padding: 16px;
  gap: 16px;
  width: 100%;
  height: 100%;

  @media (max-width: 1200px) {
    flex-direction: column;
    gap: 20px;
  }
`;

export const Sidebar = styled.aside`
  background: var(--surface);
  border-radius: 25px;
  padding: 25px;
  min-width: 250px;
  border: 2px solid var(--accent-light);
  box-shadow: var(--shadow-soft);
  height: fit-content;
  position: sticky;
  /* Mora biti <= (visina headera + padding layouta), inače sticky gura
     sidebar ispod linije kontenta već na vrhu stranice */
  top: 100px;
  animation: ${slideInFromLeft} 0.6s ease-out 0.4s both;
  transition: all 0.3s ease;

  &:hover {
    /* border-color: var(--accent); */
    background: var(--surface);
  }

  @media (max-width: 1200px) {
    position: static;
  }

  @media (max-width: 550px) {
    display: none;
  }
`;

export const SidebarTitle = styled.h3`
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--text-primary);
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const SidebarNav = styled.ul`
  list-style: none;
`;
export const AdminLogo = styled.img`
  max-height: 45px;
`;

export const LogoButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
`;

export const MobileMenuButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: none;
  align-items: center;
  justify-content: center;

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }

  @media (max-width: 550px) {
    display: flex;
  }
`;

export const NavItem = styled.li`
  margin-bottom: 8px;
`;

export const NavLink = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 15px;
  border-radius: 15px;
  text-decoration: none;
  text-align: left;
  font-family: inherit;
  color: ${(props) =>
    props.$active ? "var(--surface)" : "var(--text-secondary)"};
  font-size: 13px;
  transition: all 0.3s ease;
  border: 1px solid
    ${(props) => (props.$active ? "var(--accent)" : "var(--accent-light)")};
  font-weight: 500;
  cursor: pointer;
  background: ${(props) =>
    props.$active ? "var(--gradient-primary)" : "var(--surface)"};

  &:hover {
    background: var(--gradient-primary);
    color: var(--surface);
    border-color: var(--accent);
    transform: translateX(5px);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
`;

export const MainContent = styled.main`
  background: var(--surface);
  border-radius: 25px;
  padding: 25px;
  border: 2px solid var(--accent-light);
  animation: ${slideInFromRight} 0.6s ease-out 0.4s both;
  box-shadow: var(--shadow-soft);
  transition: all 0.3s ease;
  flex: 1;
  height: 100%;
  @media (max-width: 1200px) {
    padding: ${(props) => (props.$noPadding ? "0" : "25px")};
  }
  @media (max-width: 768px) {
    padding: ${(props) => (props.$noPadding ? "0" : "20px")};
  }
`;

// Mobile Menu Components
export const UserMobileMenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  z-index: 999;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  transition:
    opacity 0.3s ease,
    visibility 0.3s ease;
`;

export const UserMobileMenuContainer = styled.div`
  position: fixed;
  top: 72.8px;
  right: 0px;
  width: calc(100% - 40px);
  max-width: 350px;
  background: var(--surface);
  border-radius: 20px;
  border: 2px solid var(--accent-light);
  border-top: none;
  z-index: 1001;
  overflow: hidden;
  transform: ${(props) =>
    props.$isOpen ? "translateX(0) scale(1)" : "translateX(100%) scale(0.95)"};
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: top right;
  border-top-right-radius: 0px;
  border-top-left-radius: 0px;
`;

export const UserMobileMenuHeader = styled.div`
  padding: 20px 25px;
  border-bottom: 2px solid var(--accent-light);
`;

export const UserMobileUserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const UserMobileAvatar = styled.div`
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: var(--gradient-primary);
  color: var(--surface);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 16px;
  border: 2px solid var(--accent-light);
`;

export const UserMobileDetails = styled.div`
  h4 {
    font-size: 16px;
    font-weight: 600;
    margin: 0;
    color: var(--text-primary);
  }

  p {
    font-size: 12px;
    margin: 0;
    color: var(--text-secondary);
    text-transform: uppercase;
    font-weight: 500;
  }
`;

export const UserMobileNav = styled.nav`
  padding: 20px 0;

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
`;

export const UserMobileNavLink = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 15px 25px;
  border: none;
  text-decoration: none;
  text-align: left;
  font-family: inherit;
  color: var(--accent);
  font-weight: ${(props) => (props.$active ? "700" : "500")};
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  background: ${(props) =>
    props.$active ? "var(--secondary)" : "transparent"};

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    height: 60%;
    width: ${(props) => (props.$active ? "3px" : "0")};
    background: var(--gradient-primary);
    transition: width 0.2s ease;
    border-radius: 0 2px 2px 0;
  }

  &:hover {
    background: var(--accent-light);
    padding-left: 30px;
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: -2px;
  }

  svg {
    font-size: 16px;
  }
`;

export const UserMobileMenuDivider = styled.div`
  height: 2px;
  background: var(--accent-light);
  margin: 15px 20px;
`;

export const UserMobileActions = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const UserMobileLogoutBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 20px;
  background: var(--gradient-primary);
  border: none;
  border-radius: 25px;
  color: var(--surface);
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: var(--shadow-soft);
  font-family: inherit;

  svg {
    font-size: 16px;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: var(--shadow-medium);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;
